import { useCallback, useEffect, useMemo, useRef } from "react";
import { nanoid } from "nanoid/non-secure";
export var componentToLabelSize = {
  small: "small",
  medium: "large",
  large: "large"
};
export var useAutoId = function useAutoId(id) {
  return useRef(id || nanoid(5)).current;
};
export var useUpdateEffect = function useUpdateEffect(callback, deps) {
  var hasMount = useRef(false);
  useEffect(function () {
    if (hasMount.current) {
      callback();
    } else {
      hasMount.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};
export var usePrevious = function usePrevious(value) {
  var ref = useRef();
  useEffect(function () {
    ref.current = value;
  });
  return ref.current;
};
export var deprecationWarning = function deprecationWarning(msg) {
  if (typeof window === "object" && window.location.hostname === "localhost" || typeof process === "object" && process.env && process.env.NODE_ENV === "development") {
    console.warn("%c\uD83D\uDEA7 Construct Kit deprecation warning:%c\n" + msg, "font-weight: bold", "font-weight: normal");
  }
};
export var debounce = function debounce(func, waitFor) {
  if (waitFor === void 0) {
    waitFor = 200;
  }
  var timeout;
  return {
    debounceFunc: function debounceFunc() {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      if (timeout) {
        clearTimeout(timeout);
      }
      timeout = window.setTimeout(function () {
        func.apply(void 0, args);
      }, waitFor);
    },
    cancel: function cancel() {
      if (timeout) {
        clearTimeout(timeout);
      }
    }
  };
};
export var useDebouncedCallback = function useDebouncedCallback(func, waitFor) {
  if (waitFor === void 0) {
    waitFor = 200;
  }
  var funcRef = useRef(func);
  funcRef.current = func;
  var debouncedFunc = useMemo(function () {
    return debounce(funcRef.current, waitFor).debounceFunc;
  }, [waitFor]);
  return useCallback(function () {
    return debouncedFunc.apply(void 0, arguments);
  }, [debouncedFunc]);
};
export var replaceToken = function replaceToken(tokens, value) {
  // improve performance using direct look up for early return
  return tokens[value] || Object.keys(tokens).filter(function (key) {
    return value.includes(key);
  }).reduce(function (v, t) {
    return v.replace(new RegExp("\\b" + t + "\\b", "g"), tokens[t]);
  }, value);
};
export var pxToRem = function pxToRem(px) {
  return px / 16 + "rem";
};

// This is a custom prop-types validator for our dangerouslySetClassNames prop.
// It checks all the keys passed are valid and that their values are strings.
// The `complexKeys` are for situations that where we accept dangerouslySetClassNames
// for other components (e.g. `labelClassNames`), for these keys, we check their values are objects.
// See https://git.realestate.com.au/construct-kit/construct-kit-web/issues/1467 for more context
export var isDangerouslySetClassNamesType = function isDangerouslySetClassNamesType(keys, complexKeys) {
  return function (props, propName, componentName) {
    var dangerouslySetClassNames = props[propName];
    var allKeys = complexKeys ? [].concat(keys, complexKeys) : keys;
    if (!dangerouslySetClassNames) return null;
    var keyErrors = Object.keys(dangerouslySetClassNames).reduce(function (errors, key) {
      return !allKeys.includes(key) ? [].concat(errors, ["Invalid key `" + key + "` passed to `" + propName + "` on `" + componentName + "`."]) : errors;
    }, []);
    var valueErrors = Object.keys(dangerouslySetClassNames).reduce(function (errors, key) {
      var value = dangerouslySetClassNames[key];
      if (complexKeys != null && complexKeys.includes(key)) return typeof value !== "object" ? [].concat(errors, ["Invalid type of `" + typeof value + "` passed as value to key `" + key + "` in `" + propName + "` prop on `" + componentName + "`. Expected object matching dangerouslySetClassNames type from " + key.replace("ClassNames", "") + " component"]) : errors;else return typeof value !== "string" && typeof value !== "undefined" ? [].concat(errors, ["Invalid type of `" + typeof value + "` passed as value to key `" + key + "` in `" + propName + "` prop on `" + componentName + "`."]) : errors;
    }, []);
    var errors = [].concat(keyErrors, valueErrors);
    return errors.length > 0 ? new Error(errors.join("\n\n")) : null;
  };
};
var step = function step(element, startX, startY, endX, endY, startTime) {
  // Based on polyfill - https://github.com/iamdustan/smoothscroll/blob/master/src/smoothscroll.js
  var MAGIC = 468;
  var elapsedTime = (Date.now() - startTime) / MAGIC;
  var progress = elapsedTime > 1 ? 1 : elapsedTime;
  var multiplier = 0.5 * (1 - Math.cos(Math.PI * progress));
  var nextX = startX + (endX - startX) * multiplier;
  var nextY = startY + (endY - startY) * multiplier;
  element.scrollLeft = nextX;
  element.scrollTop = nextY;
  if (nextX !== endX || nextY !== endY) {
    window.requestAnimationFrame(function () {
      return step(element, startX, startY, endX, endY, startTime);
    });
  }
};
export var smoothScrollElement = function smoothScrollElement(element, x, y) {
  if ("scrollBehavior" in document.documentElement.style) {
    if (x !== null) element.scrollLeft = x;
    if (y !== null) element.scrollTop = y;
  } else {
    var startX = element.scrollLeft;
    var startY = element.scrollTop;
    var endX = x === null ? startX : x;
    var endY = y === null ? startY : y;
    step(element, startX, startY, endX, endY, Date.now());
  }
};